import { useState, useRef, useContext, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import ProductDetails from "../components/ProductDetails";
import ShopifyOrdersList from "../components/ShopifyOrdersList";
import ConfirmStartDialog from "../sections/receiving/ConfirmStartDialog";
import ProductStatus from "../sections/receiving/ProductStatus";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const ProductReception = () => {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [isLoading, setIsLoading] = useState(false);
  const [scanInput, setScanInput] = useState("");

  const [lastSync, setLastSync] = useState(null);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);

  const [cin7List, setCin7List] = useState([]);
  const [shopifyB2cList, setShopifyB2cList] = useState([]);
  const [shopifyB2bList, setShopifyB2bList] = useState([]);

  const [selectedCin7Item, setSelectedCin7Item] = useState(null);
  const [selectedShopifyB2cItem, setSelectedShopifyB2cItem] = useState(null);
  const [selectedShopifyB2bItem, setSelectedShopifyB2bItem] = useState(null);

  const inputRef = useRef();

  const findLastSync = async () => {
    try {
      const res = await fetchWithInterceptor(`/api/receiving/last-sync`, {
        method: "GET",
        headers: {
          "x-access-token": authContext.getToken(),
        },
      });

      if (res.ok) {
        const data = await res.json();
        setLastSync(data);
      }
    } catch (err) {
      console.error("Error fetching last sync", err);
    }
  };

  const handleRefresh = async () => {
    setRefreshDisabled(true);
    await findLastSync();
    setTimeout(() => setRefreshDisabled(false), 15000);
  };

  const hideConfirmStart = () => {
    setConfirmStart(false);
    handleRefresh();
  };

  const resetValues = () => {
    setCin7List([]);
    setShopifyB2cList([]);
    setShopifyB2bList([]);

    setSelectedCin7Item(null);
    setSelectedShopifyB2cItem(null);
    setSelectedShopifyB2bItem(null);
  };

  const handleProductSearch = async (query) => {
    if (!query.trim()) return;

    setIsLoading(true);

    resetValues();

    try {
      const res = await fetchWithInterceptor(
        `/api/receiving/product/${encodeURIComponent(query.trim())}`,
        {
          method: "GET",
          headers: {
            "x-access-token": authContext.getToken(),
          },
        }
      );

      const data = await res.json();

      // Product not found
      if (!res.ok || !data?.cin7?.length) {
        appContext.showMessage(
          "No products found for the scanned code.",
          "info"
        );
        return;
      }

      // Multiple Cin7 products found - require selection
      if (data.cin7?.length > 1) {
        setCin7List(data.cin7);
        return;
      }

      const shopifyB2cItems = data.shopify?.b2c || [];
      const shopifyB2bItems = data.shopify?.b2b || [];

      if (!shopifyB2cItems?.length && !shopifyB2bItems?.length) {
        appContext.showMessage(
          "No products found for the scanned code.",
          "info"
        );
        return;
      }

      // No Shopify B2C products found - inform user
      if (shopifyB2cItems.length === 0) {
        resetValues();
        appContext.showMessage(
          "Product exists in Cin7 but is not yet created in Shopify (B2C/B2B).",
          "info"
        );
        return;
      }

      // Single Cin7 product found - proceed
      setSelectedCin7Item(data.cin7[0]);

      if (shopifyB2cItems.length > 1) {
        // Multiple Shopify B2C products found - require selection
        setShopifyB2cList(shopifyB2cItems);
      } else if (shopifyB2cItems.length === 1) {
        // Single Shopify B2C product found - select it
        setSelectedShopifyB2cItem(shopifyB2cItems[0]);
      }

      if (shopifyB2bItems.length > 1) {
        // Multiple Shopify B2B products found - require selection
        setShopifyB2bList(shopifyB2bItems);
      } else if (shopifyB2bItems.length === 1) {
        // Single Shopify B2B product found - select it
        setSelectedShopifyB2bItem(shopifyB2bItems[0]);
      }
    } catch (err) {
      appContext.showMessage("Error searching for product.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCin7Selection = async (item) => {
    setCin7List([]);
    setSelectedCin7Item(item);

    setIsLoading(true);

    try {
      const res = await fetchWithInterceptor(
        `/api/receiving/product/shopify/${encodeURIComponent(item.sku)}`,
        {
          method: "GET",
          headers: { "x-access-token": authContext.getToken() },
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        appContext.showMessage(errorData.message, "error");
        resetValues();
        setIsLoading(false);
        return;
      }

      const data = await res.json();
      const shopifyB2cItems = data.shopify?.b2c || [];
      const shopifyB2bItems = data.shopify?.b2b || [];

      // No Shopify B2C products found - inform user
      if (shopifyB2cItems.length === 0) {
        resetValues();
        appContext.showMessage(
          "Product exists in Cin7 but is not yet created in Shopify (B2C/B2B).",
          "info"
        );
        return;
      }

      if (shopifyB2cItems.length > 1) {
        // Multiple Shopify B2C products found - require selection
        setShopifyB2cList(shopifyB2cItems);
      } else if (shopifyB2cItems.length === 1) {
        // Single Shopify B2C product found - select it
        setSelectedShopifyB2cItem(shopifyB2cItems[0]);
      }

      if (shopifyB2bItems.length > 1) {
        // Multiple Shopify B2B products found - require selection
        setShopifyB2bList(shopifyB2bItems);
      } else if (shopifyB2bItems.length === 1) {
        // Single Shopify B2B product found - select it
        setSelectedShopifyB2bItem(shopifyB2bItems[0]);
      }
    } catch (err) {
      appContext.showMessage("Error searching in Shopify", "error");
      resetValues();
    } finally {
      setIsLoading(false);
    }
  };

  const handleShopifySelection = (product, type) => {
    if (type === "b2c") {
      setShopifyB2cList([]);
      setSelectedShopifyB2cItem(product);
    } else if (type === "b2b") {
      setShopifyB2bList([]);
      setSelectedShopifyB2bItem(product);
    }
  };

  const handleProductUpdate = (newValues) => {
    setSelectedCin7Item((prev) => ({ ...prev, ...newValues.cin7 }));
    setSelectedShopifyB2cItem((prev) => ({
      ...prev,
      ...newValues.shopifyB2c,
    }));
    if (newValues.shopifyB2b) {
      setSelectedShopifyB2bItem((prev) => ({
        ...prev,
        ...newValues.shopifyB2b,
      }));
    }
  };

  const handleDismiss = () => {
    setScanInput("");

    resetValues();
  };

  const focusOnBarcode = () => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  useEffect(() => {
    focusOnBarcode();

    findLastSync();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLastSync = () => {
    if (lastSync?.isSyncing) {
      return <Typography>Last sync: In progress...</Typography>;
    }
    if (lastSync?.syncedAt) {
      return (
        <Typography>
          Last sync: {new Date(lastSync.syncedAt).toLocaleString()}
        </Typography>
      );
    }
    return <Typography>Last sync: Never</Typography>;
  };

  const renderSyncButton = () => {
    if (lastSync?.isSyncing) {
      return (
        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          disabled={refreshDisabled}
        >
          Refresh
        </Button>
      );
    }
    return (
      <Button
        variant="outlined"
        color="primary"
        onClick={() => setConfirmStart(true)}
        disabled={refreshDisabled}
      >
        Sync Now
      </Button>
    );
  };

  const renderSelection = () => {
    if (cin7List.length > 1) {
      return (
        <Card sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Multiple Cin7 products found — select one:
          </Typography>

          {cin7List.map((p, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                borderBottom: "1px solid #eee",
                "&:hover": { cursor: "pointer" },
              }}
              onClick={() => handleCin7Selection(p)}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {p.productName}
              </Typography>
              <Typography variant="body2">SKU: {p.sku}</Typography>
              <Typography variant="body2">Barcode: {p.barcode}</Typography>
            </Box>
          ))}
        </Card>
      );
    }
    if (shopifyB2cList.length > 1) {
      return (
        <Card sx={{ mb: 3, p: 2, border: "1px solid #ccc" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Multiple Shopify B2C products found — select one:
          </Typography>

          {shopifyB2cList.map((product, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                borderBottom: "1px solid #eee",
                "&:hover": { cursor: "pointer" },
              }}
              onClick={() => handleShopifySelection(product, "b2c")}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {product.productName}
              </Typography>
              <Typography variant="body2">SKU: {product.sku}</Typography>
              <Typography variant="body2">
                Barcode: {product.barcode}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Status:</Typography>
                <ProductStatus status={product.status} />
              </Box>
            </Box>
          ))}
        </Card>
      );
    }
    if (shopifyB2bList.length > 1) {
      return (
        <Card sx={{ mb: 3, p: 2, border: "1px solid #ccc" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Multiple Shopify B2B products found — select one:
          </Typography>

          {shopifyB2bList.map((product, idx) => (
            <Box
              key={idx}
              sx={{
                p: 1.5,
                borderBottom: "1px solid #eee",
                "&:hover": { cursor: "pointer" },
              }}
              onClick={() => handleShopifySelection(product, "b2b")}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {product.productName}
              </Typography>
              <Typography variant="body2">SKU: {product.sku}</Typography>
              <Typography variant="body2">
                Barcode: {product.barcode}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Status:</Typography>
                <ProductStatus status={product.status} />
              </Box>
            </Box>
          ))}
        </Card>
      );
    }
    return null;
  };

  const renderContent = () => {
    if (
      !!selectedCin7Item &&
      !!selectedShopifyB2cItem &&
      !shopifyB2bList.length
    ) {
      return (
        <Card sx={{ width: "100%", maxWidth: "100%" }}>
          <CardContent>
            <ProductDetails
              selectedCin7Item={selectedCin7Item}
              selectedShopifyB2cItem={selectedShopifyB2cItem}
              selectedShopifyB2bItem={selectedShopifyB2bItem}
              onProductUpdate={handleProductUpdate}
            />
            <ShopifyOrdersList productSku={selectedCin7Item?.sku} />
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDismiss}
                fullWidth
              >
                Dismiss
              </Button>
            </Box>
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
        >
          <Typography variant="body1" color="textSecondary">
            Scan a product's barcode to verify its information.
          </Typography>
          <Box sx={{ flex: 1, maxWidth: 600 }}>
            <TextField
              fullWidth
              label="Barcode or SKU"
              variant="outlined"
              size="small"
              value={scanInput}
              inputRef={inputRef}
              onChange={(e) => {
                const value = e.target.value;
                setScanInput(value);
                if (value.length >= 3) handleProductSearch(value);
              }}
            />
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            {renderLastSync()}
            {renderSyncButton()}
          </Box>
        </Box>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {renderSelection()}

        {renderContent()}
      </Box>
      <ConfirmStartDialog open={confirmStart} onClose={hideConfirmStart} />
    </>
  );
};

export default ProductReception;
